import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { syncApi, databasesApi } from '../../services/api';
import DevOnlyGuard from '../../components/common/DevOnlyGuard';
import syncStatusBadge from './syncStatusBadge';
import {
  Database,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  RotateCcw,
  ArrowRight,
  Layers,
  Zap,
  Copy,
  Shuffle,
  Lock,
  Ban, Loader2
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

// The hardcoded TIERS / TOTAL_TABLES tables that used to live here are gone. They were a
// hand-maintained duplicate of SyncConfiguration.cs and had already drifted — the list
// still carried 'Organization' (dropped from the backend in a17.029), so every table count
// this page displayed was wrong. Depths and table sets now come from GET /api/sync/plan,
// which derives them from the live schema.
const DEPTH_LABELS = ['Reference', 'Core', 'Dependent', 'Structural', 'Complex'];

// Terminal sync statuses — polling must stop on ALL of these, not just SUCCESS/FAILED.
//   PARTIAL — run finished but some tables failed or an FK could not be re-validated
//   BLOCKED — preflight refused to proceed; nothing was changed
const TERMINAL_STATUSES = ['SUCCESS', 'FAILED', 'PARTIAL', 'BLOCKED'];

// Phase A (AWA -> PreProd) ONLY. These describe the hand-written per-project transforms in
// SyncConfiguration.GetPhaseATables — Access-source quirks that are genuinely still
// hardcoded backend-side, so documenting them here is accurate.
//
// The former `excludedTables` field was removed: it claimed GWA synced "all 33 tables"
// when the backend list was 31 and silently skipped others. Table membership now comes
// from /api/sync/plan for the golden phases.
const PHASE_A_TRANSFORMS = {
  GWA: {
    customTransforms: [
      { table: 'Estimate', type: 'rename', desc: 'Column renames (spaces \u2192 underscores)' },
      { table: 'Networks_ENET', type: 'explicit', desc: 'Explicit column list (computed cols Line_1/2/3)' },
      { table: 'Networks_Serial', type: 'rename', desc: 'Table rename from Networks_CNET' },
      { table: 'Pwr_Dist', type: 'merge', desc: 'Dual-source merge (Pnl_Pwr_Dist + Inst_Pwr_Dist)' },
      { table: 'Tags', type: 'filter', desc: 'From Tags_Phase1, excludes Register' }
    ]
  },
  PNA: {
    customTransforms: [
      { table: 'System_Code', type: 'filter', desc: 'Excludes System_ID column' },
      { table: 'Tags', type: 'filter', desc: 'Excludes Register, Wire_Label_Line1/2' },
      { table: 'Pwr_Dist', type: 'merge', desc: 'Device_Tag cascading lookup (Panel \u2192 Tag \u2192 Load)' }
    ]
  },
  GFS: {
    customTransforms: [
      { table: 'Networks_Serial', type: 'rename', desc: 'Table rename from Networks_CNET' },
      { table: 'Tags', type: 'merge', desc: '4-phase UNION (Phase1/2/3 + UPW), auto-gen IDs' }
    ]
  }
};

// Shared frozen fallback for projects with no Phase A entry — see `meta` below.
const EMPTY_PHASE_A_META = Object.freeze({ customTransforms: Object.freeze([]) });

const DIRECTIONS = [
  { id: 'PROD_TO_PREPROD', label: 'Prod \u2192 PreProd', desc: 'Azure production to Azure preprod', enabled: true },
  { id: 'PREPROD_TO_PROD', label: 'PreProd \u2192 Prod', desc: 'Azure preprod to Azure production', enabled: true },
  { id: 'AWA_TO_PREPROD', label: 'AWA Prod \u2192 Azure PreProd', desc: 'On-prem production to Azure preprod (with transforms)', enabled: false, disabledDesc: 'Disabled: DAWN is live.' }
];

// Directions whose target is Azure Production \u2014 a real (non-dry) run requires an acknowledgement.
const PROD_TARGET_DIRECTIONS = ['PREPROD_TO_PROD'];

const TRANSFORM_ICONS = {
  rename: Shuffle,
  explicit: Layers,
  merge: Zap,
  filter: Copy
};

// Badge colour per transform type. Straight copies and excluded tables are gray; merge is
// purple rather than red so it does not read as an error.
const TRANSFORM_BADGES = {
  rename:   'stui-badge-orange',
  explicit: 'stui-badge-indigo',
  merge:    'stui-badge-purple',
  filter:   'stui-badge-teal'
};
const getTransformBadge = (type) => TRANSFORM_BADGES[String(type || '').toLowerCase()] || 'stui-badge-gray';

// Badge colour per dependency tier (T0, T1, ...). Depths past 4 share the last colour; T3 is
// blue rather than red so a tier never reads as an error.
const TIER_BADGES = ['stui-badge-indigo', 'stui-badge-teal', 'stui-badge-orange', 'stui-badge-blue', 'stui-badge-purple'];
const getTierBadge = (depth) => TIER_BADGES[Math.min(Math.max(Number(depth) || 0, 0), TIER_BADGES.length - 1)];

const SyncDashboardPage = () => {
  // Setup state
  const [databases, setDatabases] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(() => DIRECTIONS.find(d => d.enabled).id);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  // Execute state
  const [dryRun, setDryRun] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const pollingRef = useRef(null);

  // Validation state
  const [expandedTiers, setExpandedTiers] = useState({});
  const [expandedResults, setExpandedResults] = useState(true);

  // Backend-derived: table set + depths for the selected project/direction, and whether
  // Data Sync can run here at all.
  const [availability, setAvailability] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);

  // PHASE_A_TRANSFORMS only covers the three AWA-sourced legacy projects. Anything
  // provisioned through DAWN (T1EG2, TMPLT) has no on-prem Access source and therefore no
  // Phase A transforms at all, so a lookup miss is the normal case, not an error. Those two
  // only became selectable once the dropdown stopped requiring ConnStringName, which is what
  // turned the miss into a crash. Default to an empty list so the render path never
  // dereferences undefined — an empty array already renders as "no transforms".
  const meta = (selectedProject
    ? PHASE_A_TRANSFORMS[selectedProject] ?? PHASE_A_TRANSFORMS[selectedProject.toUpperCase()]
    : null) ?? EMPTY_PHASE_A_META;

  // Group the plan's tables by derived depth, replacing the old hardcoded TIERS.
  const planTiers = useMemo(() => {
    if (!plan?.tables) return [];
    const byDepth = {};
    plan.tables.forEach(t => { (byDepth[t.depth] ||= []).push(t.table); });
    return Object.keys(byDepth)
      .map(Number)
      .sort((a, b) => a - b)
      .map(d => ({
        tier: d,
        label: DEPTH_LABELS[d] || `Depth ${d}`,
        tables: byDepth[d].slice().sort()
      }));
  }, [plan]);

  const totalTables = plan?.tableCount ?? 0;

  // ── Data loading ──────────────────────────────────────────────────
  useEffect(() => {
    databasesApi.getAvailable()
      // Fall back to slug: databases provisioned through the DAWN admin UI are created
      // with ConnStringName = NULL (ProvisioningService never sets it), which used to
      // hide them here entirely — that's why T1EG2 and TMPLT never appeared. The backend
      // resolver accepts either token, so slug is a valid project identifier.
      .then(res => setDatabases(res.data?.filter(d => d.connStringName || d.slug) || []))
      .catch(() => setDatabases([]));
  }, []);

  // Can Data Sync run in this deployment at all?
  useEffect(() => {
    syncApi.getAvailability()
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability({ available: false, reason: 'Unable to reach the sync service.', environments: [], phases: [] }));
  }, []);

  // Table set + derived depths for the current selection. Golden phases only — Phase A's
  // table list is hand-written per project in SyncConfiguration and isn't schema-derived.
  useEffect(() => {
    if (!selectedProject || !selectedDirection || selectedDirection === 'AWA_TO_PREPROD') {
      setPlan(null); setPlanError(null); return;
    }
    let cancelled = false;
    setPlanLoading(true); setPlanError(null);
    syncApi.getPlan(selectedProject, selectedDirection)
      .then(res => { if (!cancelled) setPlan(res.data); })
      .catch(err => {
        if (cancelled) return;
        setPlan(null);
        setPlanError(err?.response?.data?.error || 'Could not derive the table plan.');
      })
      .finally(() => { if (!cancelled) setPlanLoading(false); });
    return () => { cancelled = true; };   // guard against a stale response overwriting a newer one
  }, [selectedProject, selectedDirection]);

  // A prod acknowledgement covers one project + direction only; any change invalidates it.
  useEffect(() => { setAcknowledged(false); }, [selectedProject, selectedDirection]);

  const loadHistory = useCallback(async (project) => {
    if (!project) return;
    setHistoryLoading(true);
    try {
      const res = await syncApi.getHistory(project);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  // Load history when project changes
  useEffect(() => {
    if (selectedProject) {
      loadHistory(selectedProject);
    }
  }, [selectedProject, loadHistory]);

  // ── Polling ───────────────────────────────────────────────────────
  // Use refs to avoid stale closures in the polling interval
  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const res = await syncApi.getStatus();
      setSyncStatus(res.data);
      if (TERMINAL_STATUSES.includes(res.data?.status)) {
        stopPolling();
        const proj = selectedProjectRef.current;
        if (proj) loadHistory(proj);
      }
    } catch { /* ignore */ }
  }, [loadHistory, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollStatus, 1500);
  }, [pollStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Check if there's already a running sync on mount
  useEffect(() => {
    pollStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ───────────────────────────────────────────────────────
  const startSync = async () => {
    if (!selectedProject || !selectedDirection) return;
    const request = {
      Project: selectedProject,
      Phase: selectedDirection,
      DryRun: dryRun
    };
    try {
      const res = await syncApi.start(request);
      setSyncStatus(res.data);
      // Start polling immediately + on interval
      startPolling();
      // Also do an immediate poll after a short delay (catches fast dry runs)
      setTimeout(pollStatus, 500);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage || err.message;
      setSyncStatus({ status: 'FAILED', errorMessage: msg, project: selectedProject });
    }
  };

  const acknowledge = () => {
    setSyncStatus(null);
    setExpandedResults(true);
    setExpandedTiers({});
    setDryRun(false);
    setAcknowledged(false);
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const isRunning = syncStatus?.status === 'RUNNING';
  const isComplete = TERMINAL_STATUSES.includes(syncStatus?.status);
  const isCompleteForThisProject = isComplete && syncStatus?.project === selectedProject;
  const hasPendingAck = isComplete && syncStatus?.project && syncStatus.project !== selectedProject;

  const showExecuteTile = selectedProject && selectedDirection;
  const showValidationTile = isCompleteForThisProject;

  // Can't start a new sync while another is running. A real run into Prod also needs the
  // acknowledgement; a dry run writes nothing, so it skips that gate.
  const isProdTarget = PROD_TARGET_DIRECTIONS.includes(selectedDirection);
  const canStartSync = !isRunning && (!isProdTarget || dryRun || acknowledged);

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const getTransformType = (tableName) => {
    if (!meta) return null;
    return meta.customTransforms.find(t => t.table === tableName);
  };

  // A table is "excluded" only if the backend says so — either it exists on just one side
  // or it matched the exclusion policy. No more per-project guesswork in the frontend.
  const excludedNames = useMemo(() => {
    const s = new Set();
    (plan?.sourceOnly || []).forEach(x => s.add(x.table));
    (plan?.destOnly || []).forEach(x => s.add(x.table));
    return s;
  }, [plan]);
  const isTableExcluded = (tableName) => excludedNames.has(tableName);

  const getTierForTable = (tableName) =>
    planTiers.find(t => t.tables.includes(tableName)) || null;

  // ── Render ────────────────────────────────────────────────────────
  // Block the whole section, not just the tiles. Same treatment as the other dev-only pages.
  if (availability && !availability.available) {
    return <DevOnlyGuard pageName="Data Sync" message={availability.reason} />;
  }

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Database size={20} className="stui-imgupload-title-icon" />
            Database Sync
          </h1>
          {plan && (
            <span className="dev-module-meta">
              {totalTables} tables{excludedNames.size > 0 ? ` (${excludedNames.size} N/A)` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="dev-module-content">

        <div className="stui-steps-layout">
          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="stui-step-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">Select database and sync direction.</p>

            {/* Database selector */}
            <StuiSelect
              className="stui-field-input"
              value={selectedProject || ''}
              disabled={isRunning}
              onChange={e => {
                setSelectedProject(e.target.value || null);
                setExpandedTiers({});
              }}
            >
              <option value="">— Select database —</option>
              {databases.map(db => {
                const token = db.connStringName || db.slug;
                return (
                  <option key={token} value={token}>
                    {db.displayName}
                  </option>
                );
              })}
            </StuiSelect>
            {databases.length === 0 && (
              <span className="stui-text-italic">Loading databases...</span>
            )}

            {/* Direction selector */}
            {selectedProject && (
              <>
              <>
                {DIRECTIONS.map(dir => {
                  // Availability is per-phase and comes from the backend, not a hardcoded flag.
                  const avail = availability?.phases?.find(p => p.key === dir.id);
                  const enabled = dir.enabled && (avail ? avail.available : true);
                  return (
                    <label
                      key={dir.id}
                      title={!enabled ? (dir.disabledDesc || avail?.reason || 'unavailable') : undefined}
                      className={`stui-radio-card${!enabled ? ' disabled' : ''}${selectedDirection === dir.id ? ' selected' : ''}`}
                    >
                      <input
                        type="radio"
                        className="stui-radio-input"
                        name="direction"
                        value={dir.id}
                        checked={selectedDirection === dir.id}
                        onChange={() => setSelectedDirection(dir.id)}
                        disabled={!enabled || isRunning}
                      />
                      <div className="stui-form-field">
                        <span className="stui-row-8 stui-fw-semibold">
                          {dir.label}
                          {!enabled && <Lock size={12} className="stui-text-muted" />}
                        </span>
                        <span className={`stui-list-item-secondary${dir.disabledDesc ? ' stui-text-error' : ''}`}>
                          {dir.disabledDesc || (!enabled && avail?.reason ? avail.reason : dir.desc)}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </>

              {/* Plan derivation state for the golden phases */}
              {planLoading && (
                <div className="stui-row-8 stui-text-sm-muted">
                  <Loader2 size={13} className="stui-spinning" /> Deriving table set and load order...
                </div>
              )}
              {planError && (
                <div className="stui-row-8 stui-text-sm-muted stui-text-error">
                  <AlertTriangle size={13} /> {planError}
                </div>
              )}
              {plan && !planLoading && (
                <div className="stui-row-8 stui-text-sm-muted">
                  <Layers size={13} /> {plan.tableCount} tables, {plan.maxDepth + 1} dependency levels
                  {plan.cycles?.length > 0 && (
                    <span className="stui-text-warning"> — dependency cycle detected</span>
                  )}
                  {plan.sourceOnly?.length > 0 && (
                    <span className="stui-text-warning"> — {plan.sourceOnly.length} source-only table(s) skipped</span>
                  )}
                </div>
              )}
              </>
            )}

            {/* History */}
            {selectedProject && (
              <div>
                <div className="stui-subsection-divider" />
                <button
                  className="stui-btn-ghost"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="stui-mt-xs">
                    <table className="stui-inner-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Phase</th>
                          <th>Status</th>
                          <th>Tables</th>
                          <th>Rows</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{(formatTimestamp(h.started_At) || '--')}</td>
                            <td>{h.sync_Phase}</td>
                            <td>
                              <span className={syncStatusBadge(h.error_Message === 'DRY RUN' ? 'dryrun' : h.status)}>
                                {h.error_Message === 'DRY RUN' ? 'DRY RUN' : h.status}
                              </span>
                            </td>
                            <td>{h.tables_Synced || '--'}</td>
                            <td>{h.total_Rows?.toLocaleString() || '--'}</td>
                            <td>{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <div className="stui-text-italic stui-mt-xs">No sync history for {selectedProject}.</div>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`stui-step-panel ${isRunning ? 'running' : ''}`}>
          {showExecuteTile && (
            <>
              <div className="stui-step-header">
                <span className="stui-step-number">2</span>
                <h3>Execute</h3>
              </div>

              {/* Pending ack banner for another project */}
              {hasPendingAck && (
                <div className="stui-inline-notice stui-inline-notice-warning">
                  <AlertTriangle size={14} />
                  <span>
                    <strong>{syncStatus.project}</strong> sync {
                      syncStatus.status === 'SUCCESS' ? 'completed'
                      : syncStatus.status === 'PARTIAL' ? 'completed with errors'
                      : syncStatus.status === 'BLOCKED' ? 'was blocked by preflight'
                      : 'failed'} —
                    switch back to review, or{' '}
                    <button className="stui-btn-link" onClick={acknowledge}>dismiss</button>
                  </span>
                </div>
              )}

              {/* Ready to sync (not running, not complete for THIS project) */}
              {!isRunning && !isCompleteForThisProject && (
                <>
                  <p className="stui-step-desc">
                    Sync <strong>{selectedProject}</strong> from{' '}
                    {DIRECTIONS.find(d => d.id === selectedDirection)?.label || selectedDirection}.
                  </p>

                  {/* Tier summary */}
                  <div className="stui-inline-notice">
                    {planTiers.map(t => {
                      const excluded = t.tables.filter(tbl => isTableExcluded(tbl)).length;
                      const active = t.tables.length - excluded;
                      return (
                        <div key={t.tier} className={`stui-row-8${active === 0 ? ' stui-text-muted' : ''}`}>
                          <span className={getTierBadge(t.tier)}>T{t.tier}</span>
                          <span className="stui-flex-1">{t.label}</span>
                          <span className="stui-fw-semibold stui-text-muted">
                            {active}/{t.tables.length}
                            {excluded > 0 && <span className="stui-text-muted"> ({excluded} N/A)</span>}
                          </span>
                        </div>
                      );
                    })}
                    {meta.customTransforms.length > 0 && selectedDirection === 'AWA_TO_PREPROD' && (
                      <div className="stui-label-note">
                        {meta.customTransforms.length} table{meta.customTransforms.length !== 1 ? 's' : ''} with custom transforms
                      </div>
                    )}
                  </div>

                  {/* Dry run toggle + sync button */}
                  <div className="stui-row-between">
                    <label className="stui-checkbox stui-ml-auto-mr">
                      Dry run
                      <input
                        type="checkbox"
                        className="stui-checkbox-input"
                        checked={dryRun}
                        onChange={(e) => setDryRun(e.target.checked)}
                      />
                    </label>
                    <button className="stui-btn stui-btn-primary" onClick={startSync} disabled={!canStartSync}>
                      <Play size={16} />
                      {dryRun ? 'Preview Sync' : 'Start Sync'}
                    </button>
                  </div>

                  {/* Prod-target acknowledgement — required for a real run, skipped by dry run */}
                  {isProdTarget && (
                    <label className="stui-inline-notice stui-inline-notice-warning">
                      <input
                        type="checkbox"
                        className="stui-checkbox-input"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                      />
                      <span>
                        I understand this writes <strong>{selectedProject}</strong> data into
                        Azure Production and overwrites live data.
                      </span>
                    </label>
                  )}
                </>
              )}

              {/* Running state */}
              {isRunning && (
                <>
                  <div className="stui-row-8 stui-fw-semibold stui-text-primary">
                    <Loader2 size={18} className="stui-spinning" />
                    <span>Syncing {syncStatus.project}...</span>
                  </div>

                  {syncStatus.currentStep && (
                    <div className="stui-inline-notice">{syncStatus.currentStep}</div>
                  )}

                  <div className="stui-row-md stui-text-xs stui-text-muted">
                    <span className="stui-row-4"><Clock size={13} /> {formatDuration(syncStatus.elapsedSeconds)}</span>
                    {syncStatus.rowsCopied > 0 && (
                      <span className="stui-row-4">{syncStatus.rowsCopied.toLocaleString()} rows</span>
                    )}
                  </div>

                  {syncStatus.tablesTotal > 0 && (
                    <div>
                      <div className="stui-progress stui-mb-xs">
                        <div
                          className="stui-progress-bar"
                          style={{ width: `${Math.round((syncStatus.tablesCompleted / syncStatus.tablesTotal) * 100)}%` }}
                        />
                      </div>
                      <span className="stui-text-xs stui-text-muted">
                        {syncStatus.tablesCompleted}/{syncStatus.tablesTotal} tables ({Math.round((syncStatus.tablesCompleted / syncStatus.tablesTotal) * 100)}%)
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Complete state (brief — validation tile has the details) */}
              {isCompleteForThisProject && (
                <div className={`stui-inline-notice stui-inline-notice-${syncStatus.status === 'SUCCESS' ? 'success' : 'error'}`}>
                  {syncStatus.status === 'SUCCESS' ? (
                    <CheckCircle size={18} className="stui-td-success" />
                  ) : (
                    <XCircle size={18} className="stui-text-error" />
                  )}
                  <div>
                    <div className="stui-fw-semibold">{syncStatus.status === 'SUCCESS' ? 'Sync Complete' : 'Sync Failed'}</div>
                    <div className="stui-text-xs stui-text-muted">
                      {formatDuration(syncStatus.elapsedSeconds)}
                      {syncStatus.rowsCopied > 0 && ` \u2022 ${syncStatus.rowsCopied.toLocaleString()} rows`}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          </div>

          {/* ═══════════ TILE 3: VALIDATION ═══════════ */}
          <div className="stui-step-panel">
          {showValidationTile && (
            <>
              <div className="stui-step-header">
                <span className="stui-step-number">3</span>
                <h3>Validation</h3>
              </div>

              {/* Overall result */}
              <div className={`stui-inline-notice stui-inline-notice-${syncStatus.status === 'SUCCESS' ? 'success' : 'error'}`}>
                {syncStatus.status === 'SUCCESS' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                <div>
                  <div className="stui-fw-semibold">
                    {syncStatus.status !== 'SUCCESS'
                      ? 'Sync failed'
                      : syncStatus.currentStep?.includes('Dry run')
                        ? `Dry run complete — ${syncStatus.tableResults?.length || totalTables} tables verified`
                        : `${syncStatus.tableResults?.length || totalTables} tables synced successfully`}
                  </div>

                  {syncStatus.errorMessage && (
                    <div className="stui-row-8">
                      <AlertTriangle size={14} />
                      <span>{syncStatus.errorMessage}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* The "No Source Data" callout that used to live here read
                  meta.excludedTables, a field removed from PHASE_A_TRANSFORMS in a20.026
                  when exclusions moved server-side. meta stayed truthy, so the deref threw
                  on every SUCCESS render. Exclusions now come from /api/sync/plan and are
                  already surfaced per-tier below via isTableExcluded(). */}

              {/* Tier-by-tier breakdown */}
              {syncStatus.status === 'SUCCESS' && planTiers.length > 0 && (
                <>
                  {planTiers.map(tier => {
                    const isExpanded = expandedTiers[tier.tier];
                    const tierResults = (syncStatus.tableResults || []).filter(r =>
                      tier.tables.includes(r.tableName)
                    );
                    const excludedInTier = tier.tables.filter(t => isTableExcluded(t));
                    const activeInTier = tier.tables.length - excludedInTier.length;

                    return (
                      <div key={tier.tier} className="stui-results-panel">
                        <div
                          className="stui-results-panel-header"
                          onClick={() => setExpandedTiers(prev => ({ ...prev, [tier.tier]: !prev[tier.tier] }))}
                        >
                          <span className={getTierBadge(tier.tier)}>T{tier.tier}</span>
                          <span className="stui-flex-1 stui-fw-semibold">{tier.label}</span>
                          <span className="stui-text-muted">
                            {tierResults.length}/{activeInTier}
                            {excludedInTier.length > 0 && ` (+${excludedInTier.length} N/A)`}
                          </span>
                          <span className="stui-row-4">
                            {tierResults.length > 0 && tierResults.every(r => r.match !== false) ? (
                              <CheckCircle size={14} className="stui-td-success" />
                            ) : tierResults.length === 0 && activeInTier === 0 ? (
                              <Ban size={14} className="stui-c-muted" />
                            ) : (
                              <AlertTriangle size={14} className="stui-text-warning" />
                            )}
                          </span>
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>

                        {isExpanded && (
                          <table className="stui-inner-table">
                            <tbody>
                            {tier.tables.map(tableName => {
                              const excluded = isTableExcluded(tableName);
                              const result = tierResults.find(r => r.tableName === tableName);
                              const transform = getTransformType(tableName);

                              return (
                                <tr key={tableName} className={excluded ? 'stui-text-muted' : undefined}>
                                  <td>
                                    {excluded ? (
                                      <Ban size={14} className="stui-c-muted" />
                                    ) : result ? (
                                      result.match !== false ? <CheckCircle size={14} className="stui-td-success" /> : <XCircle size={14} className="stui-text-error" />
                                    ) : (
                                      <span className="stui-c-muted">--</span>
                                    )}
                                  </td>
                                  <td className="stui-w-full stui-fw-medium">{tableName}</td>
                                  <td>
                                  {excluded ? (
                                    <span className="stui-badge-gray">N/A</span>
                                  ) : transform && selectedDirection === 'AWA_TO_PREPROD' ? (
                                    <span className={getTransformBadge(transform.type)} title={transform.desc}>
                                      {React.createElement(TRANSFORM_ICONS[transform.type] || Zap, { size: 10 })}
                                      {transform.type}
                                    </span>
                                  ) : (
                                    <span className="stui-badge-gray" title="Direct column-matched copy">
                                      <ArrowRight size={10} />
                                      copy
                                    </span>
                                  )}
                                  </td>
                                  <td className="stui-cell-right stui-text-nowrap">
                                    {excluded ? '' : result?.destRows != null ? result.destRows.toLocaleString() + ' rows' : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* Expandable full table results */}
              {TERMINAL_STATUSES.includes(syncStatus.status) && syncStatus.tableResults?.length > 0 && (
                <div>
                  <div className="stui-subsection-divider" />
                  <button
                    className="stui-btn-ghost"
                    onClick={() => setExpandedResults(!expandedResults)}
                  >
                    {expandedResults ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    All table results ({syncStatus.tableResults.length})
                  </button>

                  {expandedResults && (
                    <div className="stui-mt-xs">
                      <table className="stui-inner-table">
                        <thead>
                          <tr>
                            <th>Table</th>
                            <th>Type</th>
                            <th>Rows</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncStatus.tableResults.map((t, i) => {
                            const transform = getTransformType(t.tableName);
                            const tier = getTierForTable(t.tableName);
                            return (
                              <tr key={i}>
                                <td>
                                  {tier && <span className={`${getTierBadge(tier.tier)} stui-mr-xs`}>T{tier.tier}</span>}
                                  {t.tableName}
                                </td>
                                <td>
                                  {transform && selectedDirection === 'AWA_TO_PREPROD' ? (
                                    <span className={getTransformBadge(transform.type)}>
                                      {transform.type}
                                    </span>
                                  ) : (
                                    <span className="stui-badge-gray">copy</span>
                                  )}
                                </td>
                                <td>{t.destRows?.toLocaleString() || '--'}</td>
                                <td title={(t.notes || []).join(' | ') || t.error || ''}>
                                  {t.status === 'BLOCK' ? (
                                    <span className="stui-row-4 stui-text-error"><Ban size={13} /> block</span>
                                  ) : t.status === 'FAILED' ? (
                                    <span className="stui-row-4 stui-text-error"><XCircle size={13} /> failed</span>
                                  ) : t.status === 'WILL_CHANGE' ? (
                                    <span className="stui-row-4 stui-text-primary"><ArrowRight size={13} /> will change</span>
                                  ) : t.status === 'WARN' ? (
                                    <span className="stui-row-4 stui-text-warning"><AlertTriangle size={13} /> warn</span>
                                  ) : (
                                    <CheckCircle size={14} className="stui-td-success" />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Custom transforms legend — AWA_TO_PREPROD only */}
              {syncStatus.status === 'SUCCESS' && meta?.customTransforms.length > 0 && selectedDirection === 'AWA_TO_PREPROD' && (
                <div className="stui-inline-notice">
                  <div className="stui-label-xs stui-mb-xs">CUSTOM TRANSFORMS</div>
                  {meta.customTransforms.map((t, i) => (
                    <div key={i} className="stui-row-8">
                      <span className={getTransformBadge(t.type)}>
                        {React.createElement(TRANSFORM_ICONS[t.type] || Zap, { size: 10 })}
                        {t.type}
                      </span>
                      <span className="stui-fw-semibold">{t.table}</span>
                      <span className="stui-flex-1 stui-text-muted">{t.desc}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Acknowledge button */}
              <button className="stui-btn stui-btn-primary stui-ml-auto" onClick={acknowledge}>
                <RotateCcw size={16} />
                Acknowledge &amp; Reset
              </button>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncDashboardPage;
